import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, ChevronUp, Target } from 'lucide-react';
import { formatMWK } from './tokens';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bottom sheet listing today's curated busy windows / venues AND any active
 * driver heat-map quests with their progress bar.
 * Collapsed = peek (~64px). Expanded = ~52vh.
 * Sits ABOVE the bottom nav so the nav stays visible at all times.
 */
const OpportunitiesSheet = ({ driverId }) => {
  const [items, setItems] = useState([]);
  const [quests, setQuests] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!driverId) return;
    let cancelled = false;
    Promise.all([
      fetch(`${API}/api/driver/v2/${driverId}/opportunities`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/driver/v2/${driverId}/quests`).then(r => r.json()).catch(() => null),
    ]).then(([opp, qts]) => {
      if (cancelled) return;
      if (opp?.items) setItems(opp.items);
      if (qts?.items) setQuests(qts.items);
    });
    const t = setInterval(() => {
      fetch(`${API}/api/driver/v2/${driverId}/quests`)
        .then(r => r.json())
        .then(d => { if (!cancelled && d?.items) setQuests(d.items); })
        .catch(() => { /* ignore */ });
    }, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [driverId]);

  const headlineCount = items.length + quests.length;

  return (
    <div
      data-testid="driver-opportunities-sheet"
      className={`fixed left-3 right-3 z-[45] bg-white/95 backdrop-blur-md rounded-3xl border border-gray-100 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] transition-[max-height] duration-300 ease-out overflow-hidden`}
      style={{
        bottom: 'calc(76px + env(safe-area-inset-bottom, 0))',
        maxHeight: expanded ? '52vh' : '76px',
      }}
    >
      {/* Drag handle / header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3"
        data-testid="driver-opportunities-toggle"
      >
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            {quests.length > 0 ? <Target className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
          </span>
          <div className="text-left">
            <p className="text-[13px] font-semibold text-gray-900 leading-tight">
              {quests.length > 0 ? `${quests.length} quest${quests.length === 1 ? '' : 's'} available · ` : ''}
              Today's opportunities
            </p>
            <p className="text-[11px] text-gray-500 leading-tight">
              {headlineCount} item{headlineCount === 1 ? '' : 's'} · tap to expand
            </p>
          </div>
        </div>
        <ChevronUp className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* List */}
      <div className="px-3 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(52vh - 64px)' }}>
        {/* Quests first — higher value */}
        {quests.length > 0 && (
          <ul className="space-y-2 mb-3" data-testid="driver-quests-list">
            {quests.map((q) => <QuestRow key={q.id} q={q} />)}
          </ul>
        )}

        {items.length === 0 && quests.length === 0 ? (
          <p className="text-xs text-gray-400 italic px-2 py-3">No opportunities yet today.</p>
        ) : null}

        {items.length > 0 && (
          <ul className="space-y-2" data-testid="driver-opportunities-list">
            {items.map((it, i) => (
              <li
                key={`${it.name}-${i}`}
                className="flex items-center justify-between gap-3 bg-gray-50 hover:bg-emerald-50/70 rounded-2xl p-3 transition-colors"
                data-testid="driver-opportunity-row"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-gray-900 truncate">{it.name}</p>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> {it.venue} · {it.starts_at_local}
                  </p>
                </div>
                <DemandBars level={it.demand || 1} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const QuestRow = ({ q }) => {
  const completed = !!q.completed_at;
  const pct = Math.min(100, Math.round((Number(q.trip_count || 0) / Math.max(1, q.min_trips)) * 100));
  const endsAt = (() => {
    try {
      const d = new Date(q.ends_at);
      return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  })();
  return (
    <li
      data-testid="driver-quest-row"
      className={`rounded-2xl p-3 border ${completed ? 'border-emerald-300 bg-emerald-50' : 'border-amber-200 bg-amber-50/60'}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-gray-900 truncate">{q.title}</p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" /> {q.area_name} · ends {endsAt}
          </p>
        </div>
        <span
          className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-bold tabular-nums ${
            completed ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'
          }`}
          data-testid="driver-quest-bonus"
        >
          +{formatMWK(q.bonus_mwk)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-700 ease-out ${
              completed ? 'bg-emerald-500' : 'bg-amber-500'
            }`}
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
          />
        </div>
        <span className="text-[11px] font-semibold text-gray-700 tabular-nums" data-testid="driver-quest-progress">
          {q.trip_count}/{q.min_trips}
        </span>
      </div>
      {completed && (
        <p className="text-[11px] text-emerald-700 mt-1 font-semibold">✓ Completed — bonus added to pending payout</p>
      )}
    </li>
  );
};

const DemandBars = ({ level = 1 }) => {
  const heights = ['h-1.5', 'h-3', 'h-5'];
  return (
    <div className="shrink-0 flex items-end gap-0.5" aria-label={`Demand level ${level} of 3`}>
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={`w-1.5 ${heights[n - 1]} rounded-sm ${n <= level ? 'bg-emerald-500' : 'bg-gray-200'}`}
        />
      ))}
    </div>
  );
};

export default OpportunitiesSheet;
